import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { SystemAssetSourceService } from '../services/system-asset-source.service'

export interface ImportSystemAssetsByAssetSourceResponse {
  inserted: number;
  updated: number;
  unchanged: number;
  markedDeleted: number;
  restored: number;
  message: string;
}

interface ImportSummaryItem {
  icon: string;
  text: string;
  kind: 'changed' | 'unchanged';
}

@Component({
  selector: 'app-system-assets-capture-for-asset-source',
  templateUrl: './system-assets-capture-for-asset-source.component.html',
  styleUrls: ['./system-assets-capture-for-asset-source.component.scss']
})
export class SystemAssetsCaptureForAssetSourceComponent implements OnInit {
  loading = true;
  errorMessage = '';
  response: ImportSystemAssetsByAssetSourceResponse | null = null;
  summaryItems: ImportSummaryItem[] = [];

  constructor(
    @Inject(MAT_DIALOG_DATA)
    readonly data: { assetSourceId: string; assetSourceName?: string },
    private readonly dialogRef: MatDialogRef<SystemAssetsCaptureForAssetSourceComponent>,
    private readonly systemAssetSourceService: SystemAssetSourceService
  ) {}

  ngOnInit(): void {
    const assetSourceId = String(this.data?.assetSourceId ?? '').trim();
    if (!assetSourceId) {
      this.finishWithError('Asset Source ID is missing. Unable to import system assets.');
      return;
    }

    // The service endpoint accepts only assetSourceId.
    this.systemAssetSourceService.ImportSystemAssetsByAssetSource(assetSourceId).subscribe({
      next: result => {
        this.response = this.normalizeResult(result);
        this.summaryItems = this.buildSummary(this.response);
        this.loading = false;
      },
      error: error => {
        this.finishWithError(
          this.extractErrorMessage(error) || 'Unable to import system assets from this source.'
        );
      }
    });
  }

  get hasChanges(): boolean {
    if (!this.response) {
      return false;
    }

    return this.response.inserted > 0 ||
      this.response.updated > 0 ||
      this.response.markedDeleted > 0 ||
      this.response.restored > 0;
  }

  get title(): string {
    return this.hasChanges ? 'Import completed' : 'Import results';
  }

  close(): void {
    if (this.loading) {
      return;
    }

    this.dialogRef.close({ shouldViewSystemAssets: this.hasChanges });
  }

  private normalizeResult(rawResult: unknown): ImportSystemAssetsByAssetSourceResponse {
    const result = (rawResult && typeof rawResult === 'object')
      ? rawResult as Partial<ImportSystemAssetsByAssetSourceResponse>
      : {};

    return {
      inserted: this.toCount(result.inserted),
      updated: this.toCount(result.updated),
      unchanged: this.toCount(result.unchanged),
      markedDeleted: this.toCount(result.markedDeleted),
      restored: this.toCount(result.restored),
      message: String(result.message ?? '')
    };
  }

  private buildSummary(result: ImportSystemAssetsByAssetSourceResponse): ImportSummaryItem[] {
    const items: ImportSummaryItem[] = [];

    if (result.inserted > 0) {
      items.push({ icon: 'add_circle', text: `${result.inserted} new ${this.pluralize(result.inserted, 'system asset')} inserted.`, kind: 'changed' });
    }
    if (result.updated > 0) {
      items.push({ icon: 'edit', text: `${result.updated} existing ${this.pluralize(result.updated, 'system asset')} updated.`, kind: 'changed' });
    }
    if (result.markedDeleted > 0) {
      items.push({ icon: 'delete_outline', text: `${result.markedDeleted} ${this.pluralize(result.markedDeleted, 'system asset')} marked as deleted.`, kind: 'changed' });
    }
    if (result.restored > 0) {
      items.push({ icon: 'restore', text: `${result.restored} previously deleted ${this.pluralize(result.restored, 'system asset')} restored.`, kind: 'changed' });
    }
    if (result.unchanged > 0) {
      items.push({ icon: 'check_circle_outline', text: `${result.unchanged} ${this.pluralize(result.unchanged, 'system asset')} unchanged.`, kind: 'unchanged' });
    }

    if (items.length === 0 && result.message) {
      items.push({ icon: 'info_outline', text: result.message, kind: 'unchanged' });
    } else if (!this.hasChangeCounts(result) && result.message && result.unchanged === 0) {
      items.push({ icon: 'info_outline', text: result.message, kind: 'unchanged' });
    }

    if (items.length === 0) {
      items.push({ icon: 'info_outline', text: 'Import completed. The server did not report any asset changes.', kind: 'unchanged' });
    }

    return items;
  }

  private hasChangeCounts(result: ImportSystemAssetsByAssetSourceResponse): boolean {
    return result.inserted > 0 || result.updated > 0 || result.markedDeleted > 0 || result.restored > 0;
  }

  private pluralize(count: number, singular: string): string {
    return count === 1 ? singular : `${singular}s`;
  }

  private toCount(value: unknown): number {
    const count = Number(value ?? 0);
    return Number.isFinite(count) && count > 0 ? Math.floor(count) : 0;
  }

  private finishWithError(message: string): void {
    this.errorMessage = message;
    this.loading = false;
  }

  private extractErrorMessage(error: unknown): string {
    if (!error || typeof error !== 'object') {
      return '';
    }
    const candidate = error as { error?: { message?: unknown }; message?: unknown };
    return String(candidate.error?.message ?? candidate.message ?? '').trim();
  }
}
